import React, { useState, lazy, Suspense } from 'react';
import usePhotoEngineerStats from './hooks/usePhotoEngineerStats';
import useActivationsByOrg from './hooks/useActivationsByOrg';
import useSignupStats from './hooks/useSignupStats';
import useMediaUploadCounts from './hooks/useMediaUploadCounts';
import useTotalOrgCount from './hooks/useTotalOrgCount';
import SmallLoader from '@/src/Common/components/SmallLoader';
import { Heading, Subheading } from '@/src/tailwindui/heading';
import moment from 'moment-timezone';
// Define interfaces for our data types
interface ChartOptions {
    chart: {
        type: string;
        stacked?: boolean;
        zoom?: {
            enabled: boolean;
        };
    };
    plotOptions?: {
        bar: {
            horizontal: boolean;
            columnWidth: string;
        };
    };
    dataLabels: {
        enabled: boolean;
    };
    stroke?: {
        curve: string;
        width: number;
    };
    title: {
        text: string;
        align: string;
        style: {
            cssClass: string;
        };
    };
    grid?: {
        row: {
            colors: string[];
            opacity: number;
        };
    };
    xaxis: {
        categories: string[];
        title: {
            text: string;
            style: {
                cssClass: string;
            };
        };
        labels: {
            style: {
                cssClass: string;
            };
        };
    };
    yaxis: {
        title: {
            text: string;
            style: {
                cssClass: string;
            };
        };
        labels: {
            style: {
                cssClass: string;
            };
        };
    };
}

interface ChartSeries {
    name: string;
    data: number[];
}

interface ChartData {
    options: ChartOptions;
    series: ChartSeries[];
}

// Dynamically import the chart component to avoid SSR issues
const Chart = lazy(() => import('react-apexcharts'));

const Card = ({
    children,
    className = '',
}: {
    children: React.ReactNode;
    className?: string;
}) => (
    <div
        className={`bg-[#f5f5f5] dark:bg-zinc-800 rounded-lg p-6 ${className}`}
    >
        {children}
    </div>
);

const CardBody = ({
    children,
    className = '',
}: {
    children: React.ReactNode;
    className?: string;
}) => <div className={className}>{children}</div>;

const CardTitle = ({ children }: { children: React.ReactNode }) => (
    <h5 className="text-lg text-[#000000] dark:text-[#FFFFFF] font-semibold mb-2">
        {children}
    </h5>
);

Card.Body = CardBody;
Card.Title = CardTitle;

const Form = {
    Group: ({
        className = '',
        children,
    }: {
        className?: string;
        children: React.ReactNode;
    }) => <div className={className}>{children}</div>,
    Label: ({ children }: { children: React.ReactNode }) => (
        <label className="block text-sm font-medium text-[#333333] dark:text-[#FFFFFF] mb-1">
            {children}
        </label>
    ),
    Select: ({
        value,
        onChange,
        children,
    }: {
        value: number;
        onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
        children: React.ReactNode;
    }) => (
        <select
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark-dropdown-menu"
            value={value}
            onChange={onChange}
        >
            {children}
        </select>
    ),
};

const Row = ({
    className = '',
    children,
}: {
    className?: string;
    children: React.ReactNode;
}) => <div className={`flex flex-row gap-4 ${className}`}>{children}</div>;

const GridRow = ({
    className = '',
    children,
}: {
    className?: string;
    children: React.ReactNode;
}) => (
    <div className={`grid grid-cols-1 md:grid-cols-4 gap-4 ${className}`}>
        {children}
    </div>
);

const Col = ({
    md,
    className = '',
    children,
}: {
    md?: number;
    className?: string;
    children: React.ReactNode;
}) => {
    const mdClass = md ? `md:w-${md}/12` : '';
    return (
        <div className={`px-4 w-full ${mdClass} ${className}`}>{children}</div>
    );
};

export default function UsageDashboard() {
    const [timeRange, setTimeRange] = useState<number>(7);

    // Original hooks
    const { data: statsData, isLoading: statsLoading } =
        usePhotoEngineerStats(timeRange);
    const { data: activationsByOrgData, isLoading: activationsByOrgLoading } =
        useActivationsByOrg(timeRange);

    // New hooks
    const { stats: signupStats, isLoading: signupLoading } = useSignupStats();
    const { data: mediaUploadCounts, isLoading: mediaUploadCountsLoading } =
        useMediaUploadCounts(timeRange);

    const { count: totalOrgCount, isLoading: totalOrgCountLoading } =
        useTotalOrgCount();

    // Format activations by org for bar chart
    const formatActivationsByOrgChartData = (): ChartData | null => {
        if (!activationsByOrgData || activationsByOrgData.length === 0)
            return null;

        const categories = activationsByOrgData.map(
            (item) => moment(item.day).format('MM/DD/YYYY') || 'Unknown',
        );

        // Make categories unique
        const uniqueCategories = Array.from(new Set(categories));

        // Get unique org names
        const orgNames = activationsByOrgData.map((item) => item.name);
        const uniqueOrgNames = Array.from(new Set(orgNames));

        // Generate series data for each org and for each item in the categories array
        const series = [
            ...uniqueOrgNames.map((orgName) => ({
                name: orgName,
                data: uniqueCategories.map(
                    (category) =>
                        activationsByOrgData
                            .filter((item) => item.name === orgName)
                            .find(
                                (item) =>
                                    moment(item.day).format('MM/DD/YYYY') ===
                                    category,
                            )?.count || 0,
                ),
            })),
        ];

        return {
            options: {
                chart: {
                    type: 'bar',
                    stacked: true,
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '55%',
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                xaxis: {
                    categories: uniqueCategories,
                    title: {
                        text: 'Organizations',
                        style: {
                            cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                        },
                    },
                    labels: {
                        style: {
                            cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                        },
                    },
                },
                yaxis: {
                    title: {
                        text: 'Activations',
                        style: {
                            cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                        },
                    },
                    labels: {
                        style: {
                            cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                        },
                    },
                },
                title: {
                    text: 'Activations by Organization',
                    align: 'center',
                    style: {
                        cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                    },
                },
            },
            series,
        };
    };

    // Format time series data for line charts
    const formatTimeSeriesData = (
        data: any[] | undefined,
        title: string,
        yAxisTitle: string,
    ): ChartData | null => {
        if (!data || data.length === 0) return null;

        const series = [
            {
                name: yAxisTitle,
                data: data.map((item) => item.count),
            },
        ];

        return {
            options: {
                chart: {
                    type: 'line',
                    zoom: {
                        enabled: true,
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    curve: 'smooth',
                    width: 3,
                },
                title: {
                    text: title,
                    align: 'center',
                    style: {
                        cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                    },
                },
                grid: {
                    row: {
                        colors: ['#f3f3f3', 'transparent'],
                        opacity: 0.5,
                    },
                },
                xaxis: {
                    categories: data.map((item) => item.date),
                    title: {
                        text: 'Date',
                        style: {
                            cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                        },
                    },
                    labels: {
                        style: {
                            cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                        },
                    },
                },
                yaxis: {
                    title: {
                        text: yAxisTitle,
                        style: {
                            cssClass: 'dark-text',
                        },
                    },
                    labels: {
                        style: {
                            cssClass: 'dark-text',
                        },
                    },
                },
            },
            series,
        };
    };

    // Format data for org-based bar charts (total activations, shares, media uploads by org)
    const formatOrgBarChartData = (
        data: any[] | null | undefined,
        title: string,
        seriesName: string,
    ): ChartData | null => {
        if (!data || data.length === 0) return null;

        // Sort by count (highest first) and take top 10
        const sortedData = [...data]
            .sort((a, b) => Number(b.count || 0) - Number(a.count || 0))
            .slice(0, 10);

        const categories = sortedData.map(
            (item) => item.name || `Org ID: ${item.org_id || 'Unknown'}`,
        );
        const series = [
            {
                name: seriesName,
                data: sortedData.map((item) => Number(item.count || 0)),
            },
        ];

        return {
            options: {
                chart: {
                    type: 'bar',
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '55%',
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                xaxis: {
                    categories,
                    title: {
                        text: 'Organizations',
                        style: {
                            cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                        },
                    },
                    labels: {
                        style: {
                            cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                        },
                    },
                },
                yaxis: {
                    title: {
                        text: seriesName,
                        style: {
                            cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                        },
                    },
                    labels: {
                        style: {
                            cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                        },
                    },
                },
                title: {
                    text: title,
                    align: 'center',
                    style: {
                        cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                    },
                },
            },
            series,
        };
    };

    // Format data for shares by date
    const formatSharesByDateData = (
        data: any[] | null | undefined,
    ): ChartData | null => {
        if (!data || data.length === 0) return null;

        // Group by day and sum counts
        const dataByDay = data.reduce((acc, curr) => {
            const date = curr.day || 'Unknown';
            if (!acc[date]) {
                acc[date] = 0;
            }
            acc[date] += Number(curr.count || 0);
            return acc;
        }, {} as Record<string, number>);

        // Convert to array of [date, count] pairs sorted by date
        const sortedDates = Object.keys(dataByDay).sort();

        const series = [
            {
                name: 'Shares',
                data: sortedDates.map((date) => dataByDay[date]),
            },
        ];

        return {
            options: {
                chart: {
                    type: 'line',
                    zoom: {
                        enabled: true,
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    curve: 'smooth',
                    width: 3,
                },
                title: {
                    text: 'Shares Over Time (Detailed)',
                    align: 'center',
                    style: {
                        cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                    },
                },
                grid: {
                    row: {
                        colors: ['#f3f3f3', 'transparent'],
                        opacity: 0.5,
                    },
                },
                xaxis: {
                    categories: sortedDates,
                    title: {
                        text: 'Date',
                        style: {
                            cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                        },
                    },
                    labels: {
                        style: {
                            cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                        },
                    },
                },
                yaxis: {
                    title: {
                        text: 'Shares',
                        style: {
                            cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                        },
                    },
                    labels: {
                        style: {
                            cssClass: 'text-[#000000] dark:text-[#FFFFFF]',
                        },
                    },
                },
            },
            series,
        };
    };

    // Format signup stats for summary display
    const formatSignupData = () => {
        if (!signupStats) return null;

        return {
            day: signupStats.day || 0,
            week: signupStats.week || 0,
            month: signupStats.month || 0,
            year: signupStats.year || 0,
        };
    };

    const isLoading =
        statsLoading ||
        activationsByOrgLoading ||
        signupLoading ||
        mediaUploadCountsLoading;

    const UsageDashboardContent = () => {
        if (isLoading) {
            return (
                <div className="flex justify-center items-center h-screen">
                    <SmallLoader />
                </div>
            );
        }

        const activationsByOrgChart = formatActivationsByOrgChartData();

        return (
            <div className="mx-auto max-w-4xl">
                <div className="flex items-start justify-between gap-4">
                    <Heading className="text-4xl plex-sans text-[#000000] dark:text-[#FFFFFF]">
                        Usage Dashboard
                    </Heading>
                    <Form.Group className="mb-4 text-[#333333] dark:text-[#FFFFFF] bg-[#ffffff] dark:bg-[#1a1a1a] rounded-lg p-4">
                        <Form.Select
                            value={timeRange}
                            onChange={(e) =>
                                setTimeRange(Number(e.target.value))
                            }
                        >
                            <option value={7}>7 day window</option>
                            <option value={14}>14 day window</option>
                            <option value={30}>30 day window</option>
                            <option value={60}>60 day window</option>
                            <option value={90}>90 day window</option>
                        </Form.Select>
                        <div className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
                            {timeRange} days +/- today
                        </div>
                    </Form.Group>
                </div>

                <GridRow className="mb-8 flex  gap-4">
                    <Col className="mb-4 md:mb-0">
                        <Card>
                            <Card.Body className="text-center">
                                <Card.Title>Total Organizations</Card.Title>
                                <h2 className="text-3xl text-[#000000] dark:text-[#FFFFFF] monospace-font">
                                    {totalOrgCount}
                                </h2>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col className="mb-4 md:mb-0">
                        <Card>
                            <Card.Body className="text-center">
                                <Card.Title>Total Activations</Card.Title>
                                <h2 className="text-3xl text-[#000000] dark:text-[#FFFFFF] monospace-font">
                                    {statsData?.totalActivations || 0}
                                </h2>
                                <p className="text-sm text-gray-500">
                                    Today: {statsData?.activationsToday || 0}
                                </p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col className="mb-4 md:mb-0">
                        <Card>
                            <Card.Body className="text-center">
                                <Card.Title>Total Uploads</Card.Title>
                                <h2 className="text-3xl text-[#000000] dark:text-[#FFFFFF] monospace-font">
                                    {statsData?.totalMediaUploads || 0}
                                </h2>
                                <p className="text-sm text-gray-500">
                                    Today: {statsData?.mediaUploadsToday || 0}
                                </p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col className="mb-4 md:mb-0">
                        <Card>
                            <Card.Body className="text-center">
                                <Card.Title>Total Shares</Card.Title>
                                <h2 className="text-3xl text-[#000000] dark:text-[#FFFFFF] monospace-font">
                                    {statsData?.totalShares || 0}
                                </h2>
                                <p className="text-sm text-gray-500">
                                    Today: {statsData?.sharesToday || 0}
                                </p>
                            </Card.Body>
                        </Card>
                    </Col>
                </GridRow>

                <Row className="mb-8 md:grid-cols-1 gap-4">
                    <Col>
                        <Subheading className="mb-4 text-2xl plex-sans">
                            Signups
                        </Subheading>

                        <Card>
                            <Card.Body className="w-full">
                                <GridRow className="mb-8 flex  gap-4">
                                    <Col className="mb-4 md:mb-0">
                                        <div className="text-center">
                                            <h3 className="text-lg font-semibold text-[#000000] dark:text-[#FFFFFF]">
                                                Today
                                            </h3>
                                            <p className="text-2xl text-[#000000] dark:text-[#FFFFFF] monospace-font">
                                                {formatSignupData()?.day || 0}
                                            </p>
                                        </div>
                                    </Col>
                                    <Col className="mb-4 md:mb-0">
                                        <div className="text-center">
                                            <h3 className="text-lg font-semibold text-[#000000] dark:text-[#FFFFFF]">
                                                This Week
                                            </h3>
                                            <p className="text-2xl text-[#000000] dark:text-[#FFFFFF] monospace-font">
                                                {formatSignupData()?.week || 0}
                                            </p>
                                        </div>
                                    </Col>
                                    <Col className="mb-4 md:mb-0">
                                        <div className="text-center">
                                            <h3 className="text-lg font-semibold text-[#000000] dark:text-[#FFFFFF]">
                                                This Month
                                            </h3>
                                            <p className="text-2xl text-[#000000] dark:text-[#FFFFFF] monospace-font">
                                                {formatSignupData()?.month || 0}
                                            </p>
                                        </div>
                                    </Col>
                                    <Col className="mb-4 md:mb-0">
                                        <div className="text-center">
                                            <h3 className="text-lg font-semibold text-[#000000] dark:text-[#FFFFFF]">
                                                This Year
                                            </h3>
                                            <p className="text-2xl text-[#000000] dark:text-[#FFFFFF] monospace-font">
                                                {formatSignupData()?.year || 0}
                                            </p>
                                        </div>
                                    </Col>
                                </GridRow>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row className="mb-8">
                    <Col md={6} className="mb-4 md:mb-0">
                        <Subheading className="mb-4 text-2xl plex-sans">
                            Activations
                        </Subheading>
                        {activationsByOrgChart && (
                            <Card className="dark:bg-[#f5f5f5]">
                                <Card.Body className="bg-[#f5f5f5]">
                                    <Suspense
                                        fallback={
                                            <div className="flex justify-center">
                                                <SmallLoader />
                                            </div>
                                        }
                                    >
                                        <Chart
                                            options={
                                                activationsByOrgChart.options
                                            }
                                            series={
                                                activationsByOrgChart.series
                                            }
                                            type="bar"
                                            height={350}
                                        />
                                    </Suspense>
                                </Card.Body>
                            </Card>
                        )}
                    </Col>
                </Row>

                <Heading className="text-2xl plex-sans mt-12 mb-6">
                    Media Uploads by Activation
                </Heading>

                <Row className="mb-8">
                    {mediaUploadCounts && mediaUploadCounts.length > 0 ? (
                        <div className="overflow-x-auto w-full">
                            <table className="min-w-full bg-white dark:bg-gray-800">
                                <thead>
                                    <tr className="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200 uppercase text-sm">
                                        <th className="py-3 px-4 plex-sans text-left">
                                            Organization
                                        </th>
                                        <th className="py-3 px-4 plex-sans text-left">
                                            Activation
                                        </th>
                                        <th className="py-3 px-4 plex-sans text-right">
                                            Media Uploads
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mediaUploadCounts
                                        .slice(0, 10)
                                        .map((item, index) => (
                                            <tr
                                                key={index}
                                                className="border-b border-gray-200 dark:border-gray-700"
                                            >
                                                <td className="py-3 px-4 plex-sans">
                                                    {item.name ||
                                                        `Org ID: ${item.org_id}`}
                                                </td>
                                                <td className="py-3 px-4 plex-sans">
                                                    {item.activation_name ||
                                                        'Unknown'}
                                                </td>
                                                <td className="py-3 px-4 plex-sans text-right font-medium">
                                                    {item.count}
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                            {mediaUploadCounts.length > 10 && (
                                <p className="text-sm text-gray-500 mt-2 text-right">
                                    Showing top 10 of {mediaUploadCounts.length}{' '}
                                    records
                                </p>
                            )}
                        </div>
                    ) : (
                        <p className="text-center text-gray-500">
                            No media upload data available
                        </p>
                    )}
                </Row>
            </div>
        );
    };

    return (
        <div>
            <UsageDashboardContent />
        </div>
    );
}
